import {
  Play, Pause, Square, RotateCcw, Link2, X,
  Briefcase, Gamepad2, Leaf, Brain,
  Target, Monitor, TrendingUp, Award, Plus,
  Flame, CheckCircle, Minimize2, Maximize2,
  Share2, Terminal, LogOut, Sparkles, ChevronUp, ChevronDown,
  Palette,
} from "lucide-react";
import { MODE_CONFIG } from "@/lib/ModeContext";
import NotificationCenter from "@/components/NotificationCenter";
import SyncIndicator from "@/components/SyncIndicator";
import UserDropdown from "@/components/UserDropdown";
import { ProBadge } from "@/components/SubscriptionGates";
import NavTooltip from "@/components/NavTooltip";

import { FEATURE_FLAGS } from "@/config/featureFlags";

import { useTheme } from "@/lib/ThemeContext";
import { can } from "@/lib/roles";

const MODE_ICONS = { work: Briefcase, game: Gamepad2, minimalist: Leaf, adhd: Brain };

// Bundle H — Quick theme cycle button.
// Surfaces the most-used theme switch (ADHD ↔ Minimalist) one click away
// from the main app shell. Beta tester's preferred ADHD theme is now the
// default for new users; this button gives them a one-tap escape to the
// monochrome Minimalist theme without digging through Settings → Themes.
const QUICK_CYCLE = ["adhd-calm", "minimalist-bw"];

function QuickThemeToggle({ t }) {
  const { activeThemeId, setTheme } = useTheme();
  const onClick = () => {
    const idx = QUICK_CYCLE.indexOf(activeThemeId);
    const next = QUICK_CYCLE[(idx + 1) % QUICK_CYCLE.length] || QUICK_CYCLE[0];
    setTheme(next);
  };
  // Show the NEXT theme name in the tooltip so users know what they'll get.
  const isAdhd = activeThemeId === "adhd-calm";
  const tipLabel = isAdhd ? "Switch to Minimalist" : "Switch to ADHD theme";
  return (
    <NavTooltip label={tipLabel}>
      <button
        className="p-1 rounded transition-all hover:scale-110 shrink-0"
        style={{ color: t.accent }}
        onClick={onClick}
        aria-label={tipLabel}
        data-testid="quick-theme-toggle"
      >
        <Palette className="h-3 w-3" />
      </button>
    </NavTooltip>
  );
}

function Check({ className, style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function MiniTimer({ timer, modeColor, t, isGame }) {
  const size = 32;
  const r = 12;
  const circ = 2 * Math.PI * r;
  const progress = timer.mode === "stopwatch"
    ? Math.min((timer.elapsedSeconds / 3600) * 100, 100)
    : timer.modeConfig?.totalSeconds > 0
      ? ((timer.modeConfig.totalSeconds - timer.remainingSeconds) / timer.modeConfig.totalSeconds) * 100
      : 0;
  const offset = circ * (1 - Math.min(progress, 100) / 100);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`${modeColor}15`} strokeWidth={3} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={timer.isActive ? modeColor : `${t.textMuted}40`} strokeWidth={3}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.5s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-1.5 h-1.5 rounded-full"
          style={{
            backgroundColor: timer.isActive ? modeColor : t.textMuted,
            boxShadow: timer.status === "running" ? `0 0 6px ${modeColor}` : "none",
            animation: timer.status === "running" ? "crt-led-pulse 2s ease-in-out infinite" : "none",
          }} />
      </div>
    </div>
  );
}

export function TimerControls({ timer, modeColor, t }) {
  return (
    <div className="flex items-center gap-0.5 shrink-0">
      {timer.status === "idle" && (
        <NavTooltip label="Start" shortcut="space">
          <button className="p-1 rounded transition-all hover:scale-110" style={{ color: modeColor }}
            onClick={timer.start} aria-label="Start timer" data-testid="command-start"><Play className="h-3.5 w-3.5" /></button>
        </NavTooltip>
      )}
      {timer.status === "running" && (
        <NavTooltip label="Pause" shortcut="space">
          <button className="p-1 rounded transition-all hover:scale-110" style={{ color: t.accent3 }}
            onClick={timer.pause} aria-label="Pause timer" data-testid="command-pause"><Pause className="h-3.5 w-3.5" /></button>
        </NavTooltip>
      )}
      {timer.status === "paused" && (
        <NavTooltip label="Resume" shortcut="space">
          <button className="p-1 rounded transition-all hover:scale-110" style={{ color: modeColor }}
            onClick={timer.resume} aria-label="Resume timer" data-testid="command-resume"><Play className="h-3.5 w-3.5" /></button>
        </NavTooltip>
      )}
      {timer.isActive && (
        <NavTooltip label="Stop" shortcut="escape">
          <button className="p-1 rounded transition-all hover:scale-110" style={{ color: t.danger }}
            onClick={timer.stop} aria-label="Stop timer" data-testid="command-stop"><Square className="h-3 w-3" /></button>
        </NavTooltip>
      )}
      <NavTooltip label="Reset" shortcut="r">
        <button className="p-1 rounded transition-all hover:scale-110" style={{ color: t.textMuted }}
          onClick={timer.reset} aria-label="Reset timer" data-testid="command-reset"><RotateCcw className="h-3 w-3" /></button>
      </NavTooltip>
    </div>
  );
}

export function ModeMenu({ mode, setMode, showModeMenu, setShowModeMenu, t }) {
  if (!showModeMenu) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-[70] rounded-lg overflow-hidden min-w-[170px]"
      style={{ background: t.bgSecondary, border: `1px solid ${t.borderColor}`, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
      {Object.entries(MODE_CONFIG).map(([key, cfg]) => {
        const Icon = MODE_ICONS[key];
        const active = mode === key;
        return (
          <button key={key} className="w-full flex items-center gap-2 px-3 py-2 transition-all text-left"
            style={{ background: active ? `${t.accent}12` : "transparent", color: active ? t.accent : t.textPrimary }}
            onClick={() => { setMode(key); setShowModeMenu(false); }} data-testid={`mode-option-${key}`}>
            <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: active ? t.accent : t.textMuted }} />
            <span className="text-[11px] font-medium">{cfg.label}</span>
            {active && <Check className="h-3 w-3 ml-auto shrink-0" style={{ color: t.accent }} />}
          </button>
        );
      })}
    </div>
  );
}

export function LinkerDropdown({ timer, boxColors, t, onClose }) {
  const linkedBox = timer.boxes.find((b) => b.id === timer.linkedBoxId);
  const linkedColor = linkedBox ? (boxColors[linkedBox.color]?.text || t.accent) : null;
  return (
    <div className="px-4 pb-2 pt-1 mx-auto" style={{ borderTop: `1px solid ${t.borderColor}`, maxWidth: "1400px" }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: t.textSecondary }}>LINK TIMER TO</span>
        <button className="p-0.5 rounded" style={{ color: t.textMuted }} onClick={onClose}><X className="h-3 w-3" /></button>
      </div>
      <div className="flex flex-wrap gap-1">
        <button className="text-[9px] font-bold px-2 py-0.5 rounded border transition-all"
          style={{ borderColor: !timer.linkedBoxId ? t.accent : t.borderColor, color: !timer.linkedBoxId ? t.accent : t.textMuted, background: !timer.linkedBoxId ? `${t.accent}10` : "transparent" }}
          onClick={() => { timer.linkBox(null); onClose(); }} data-testid="command-link-none">GENERAL</button>
        {timer.boxes.map((box) => {
          const bc = boxColors[box.color]?.text || t.accent;
          const isActive = timer.linkedBoxId === box.id;
          return (
            <button key={box.id} className="text-[9px] font-bold px-2 py-0.5 rounded border transition-all"
              style={{ borderColor: isActive ? bc : t.borderColor, color: isActive ? bc : t.textMuted, background: isActive ? `${bc}10` : "transparent" }}
              onClick={() => timer.linkBox(box.id)} data-testid={`command-link-box-${box.id}`}>{box.title}</button>
          );
        })}
      </div>
      {linkedBox && linkedBox.tasks?.filter((tk) => !tk.completed).length > 0 && (
        <div className="mt-1 pt-1" style={{ borderTop: `1px solid ${t.borderColor}30` }}>
          <span className="text-[8px] font-bold block mb-0.5" style={{ color: t.textMuted }}>TASK:</span>
          <div className="flex flex-wrap gap-1">
            {linkedBox.tasks.filter((tk) => !tk.completed).map((task) => (
              <button key={task.id} className="text-[9px] px-2 py-0.5 rounded border transition-all"
                style={{ borderColor: timer.linkedTaskId === task.id ? linkedColor : t.borderColor, color: timer.linkedTaskId === task.id ? linkedColor : t.textMuted, background: timer.linkedTaskId === task.id ? `${linkedColor}10` : "transparent" }}
                onClick={() => { timer.linkTask(timer.linkedTaskId === task.id ? null : task.id); onClose(); }}
                data-testid={`command-link-task-${task.id}`}>{task.title}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ExpandedStats({ timer, liveStats, todayFocus, streak, byBox, boxColors, t }) {
  return (
    <div className="px-4 pb-2 pt-1 mx-auto" style={{ borderTop: `1px solid ${t.borderColor}`, maxWidth: "1400px" }}>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-2">
        <MiniStat label="TODAY" value={liveStats.tasksToday} color={t.success} testId="exp-tasks-today" />
        <MiniStat label="DONE" value={`${liveStats.completionPct}%`} color={t.accent2} testId="exp-completion" />
        <MiniStat label="FOCUS" value={`${todayFocus}m`} color={t.accent3} testId="exp-focus-today" />
        <MiniStat label="TOTAL" value={`${timer.focusStats?.total_focus_minutes ?? 0}m`} color={t.accent4} testId="exp-total-focus" />
        <MiniStat label="SESSIONS" value={timer.completedCount} color={t.danger} testId="exp-sessions" />
        <MiniStat label="STREAK" value={`${streak}d`} color={t.warning} testId="exp-streak" />
      </div>
      {byBox.length > 0 && (
        <div>
          <span className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: t.textSecondary }}>PROJECT PROGRESS</span>
          <div className="space-y-1">
            {byBox.slice(0, 4).map((b) => {
              const bc = boxColors[b.color]?.text || t.accent3;
              return (
                <div key={b.id} className="flex items-center gap-2">
                  <span className="text-[10px] w-20 truncate shrink-0" style={{ color: bc }}>{b.title}</span>
                  <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: `${bc}15` }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${b.pct}%`, backgroundColor: bc }} />
                  </div>
                  <span className="text-[9px] font-bold w-7 text-right shrink-0 tabular-nums" style={{ color: bc }}>{b.pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {timer.recentSessions.length > 0 && (
        <div className="mt-1.5">
          <span className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: t.textSecondary }}>RECENT</span>
          <div className="flex gap-1 overflow-x-auto pb-0.5">
            {timer.recentSessions.slice(0, 6).map((s) => {
              const sBox = timer.boxes.find((b) => b.id === s.box_id);
              const sc = sBox ? (boxColors[sBox.color]?.text || t.accent4) : t.accent4;
              return (
                <div key={s.id} className="shrink-0 px-1.5 py-0.5 rounded" style={{ background: `${sc}08`, border: `1px solid ${sc}15` }}>
                  <div className="text-[8px] font-bold" style={{ color: sc }}>{s.mode?.toUpperCase()}</div>
                  <div className="text-[9px] tabular-nums" style={{ color: `${sc}70` }}>{Math.round((s.actual_seconds || 0) / 60)}m</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function MiniStat({ label, value, color, testId }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: `${color}06`, border: `1px solid ${color}10` }} data-testid={testId}>
      <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: `${color}70` }}>{label}</span>
      <span className="text-[11px] font-bold tabular-nums ml-auto" style={{ color, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

export function CommandBarNav({ isDashboard, isWorkstation, isAnalytics, isRecap, navigate, t }) {
  return (
    <nav className="hidden md:flex items-center gap-0.5 ml-1" aria-label="Primary navigation" role="navigation">
      {[
        { key: "/", label: "Dashboard", icon: Target, active: isDashboard, shortcut: "g d" },
        { key: "/tasks", label: "Work", icon: Monitor, active: isWorkstation, shortcut: "g t" },
        { key: "/analytics", label: "Analytics", icon: TrendingUp, active: isAnalytics },
        { key: "/recap", label: "Recap", icon: Award, active: isRecap },
      ].map((item) => (
        <NavTooltip key={item.key} label={item.label} shortcut={item.shortcut}>
          <button
            className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-all"
            style={item.active ? { backgroundColor: `${t.accent}15`, color: t.accent } : { color: t.textMuted }}
            onClick={() => navigate(item.key)} aria-label={item.label} aria-current={item.active ? "page" : undefined}
            data-testid={`nav-${item.label.toLowerCase()}`}>
            <item.icon className="h-3 w-3" /> {item.label}
          </button>
        </NavTooltip>
      ))}
    </nav>
  );
}

export function CommandBarActions({ liveStats, streak, gameLevel, t, isPro, isCompact, setIsCompact, setShowReferral, user, logout, expanded, setExpanded }) {
  return (
    <div className="flex items-center gap-2 shrink-0 ml-3">
      <NavTooltip label="Focus streak" side="bottom">
        <div className="hidden lg:flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${t.warning}08` }} data-testid="header-streak">
          <Flame className="h-3 w-3" style={{ color: t.warning }} />
          <span className="text-[10px] font-bold tabular-nums" style={{ color: t.warning, fontFamily: "'JetBrains Mono', monospace" }}>{streak}d</span>
        </div>
      </NavTooltip>
      <NavTooltip label="Level" side="bottom">
        <div className="hidden lg:flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${t.accent}08` }} data-testid="header-xp">
          <Award className="h-3 w-3" style={{ color: t.accent }} />
          <span className="text-[10px] font-bold tabular-nums" style={{ color: t.accent, fontFamily: "'JetBrains Mono', monospace" }}>Lv{gameLevel}</span>
        </div>
      </NavTooltip>
      <NavTooltip label="Completion rate" side="bottom">
        <div className="hidden lg:flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${t.accent2}08` }} data-testid="header-momentum">
          <TrendingUp className="h-3 w-3" style={{ color: t.accent2 }} />
          <span className="text-[10px] font-bold tabular-nums" style={{ color: t.accent2, fontFamily: "'JetBrains Mono', monospace" }}>{liveStats.completionPct}%</span>
        </div>
      </NavTooltip>
      <NavTooltip label="Tasks today" side="bottom">
        <div className="hidden xl:flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${t.success}08` }} data-testid="header-today">
          <CheckCircle className="h-3 w-3" style={{ color: t.success }} />
          <span className="text-[10px] font-bold tabular-nums" style={{ color: t.success, fontFamily: "'JetBrains Mono', monospace" }}>{liveStats.tasksToday}</span>
        </div>
      </NavTooltip>
      <div className="w-px h-4 hidden md:block" style={{ background: t.borderColor }} />
      <SyncIndicator />
      <NotificationCenter />
      {isPro ? <ProBadge /> : FEATURE_FLAGS.SHOW_UPGRADE_PROMPTS ? (
        <a href="/upgrade" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 transition-all hover:scale-105"
          style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#FFFFFF" }} aria-label="Upgrade to Pro" data-testid="command-upgrade-btn">
          <Sparkles className="h-2.5 w-2.5" /> PRO
        </a>
      ) : null}
      <NavTooltip label={isCompact ? "Comfortable mode" : "Compact mode"}>
        <button className="p-1 rounded transition-all hover:scale-110 shrink-0" style={{ color: isCompact ? t.accent : t.textMuted }}
          onClick={() => setIsCompact(!isCompact)} aria-label={isCompact ? "Switch to comfortable mode" : "Switch to compact mode"} data-testid="density-toggle">
          {isCompact ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
        </button>
      </NavTooltip>
      <QuickThemeToggle t={t} />
      <NavTooltip label="Invite friends">
        <button className="p-1 rounded transition-all hover:scale-110 shrink-0" style={{ color: t.accent2 }}
          onClick={() => setShowReferral(true)} aria-label="Invite friends" data-testid="command-referral-btn">
          <Share2 className="h-3 w-3" />
        </button>
      </NavTooltip>
      {can(user, "use_dev_console") && (
        <NavTooltip label="Admin panel">
          <a href="/admin" className="p-1 rounded transition-all hover:scale-110 shrink-0" style={{ color: t.danger }} aria-label="Admin panel" data-testid="command-admin-btn">
            <Terminal className="h-3 w-3" />
          </a>
        </NavTooltip>
      )}
      <UserDropdown />
      <NavTooltip label={expanded ? "Collapse stats" : "Expand stats"}>
        <button className="p-1 rounded transition-all shrink-0" style={{ color: expanded ? t.accent : t.textMuted }}
          onClick={() => setExpanded(!expanded)} aria-label={expanded ? "Collapse stats panel" : "Expand stats panel"} data-testid="command-expand-toggle">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </NavTooltip>
    </div>
  );
}
